import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Autocomplete,
    createFilterOptions,
} from "@mui/material";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import ClientName from "./FormComponents/New/ClientName";
import ClientAddress from "./FormComponents/New/ClientAddress";
import ClientContact from "./FormComponents/New/ClientContact";
import { getSalesReps } from "../API/salesRepAPI";
import { postClient, getCacheClients } from "../API/clientAPI";
import { postLead } from "../API/leadAPI";
import { getZipCodes } from "../API/routePlannerAPI"; // Adjust the import path
import { customAlphabet } from "nanoid";
import { postJobOrder } from "../API/jobOrderAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";


function AddNewLead() {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const generateId = customAlphabet(alphabet, 8); // Creates a function to generate an 8-character ID

    // New state variables for loading and flash messages
    const [isLoading, setIsLoading] = useState(false);
    const [flashMessage, setFlashMessage] = useState();
    const [salesReps, setSalesReps] = useState([]); // State to hold sales reps
    const [clientsCache, setClientsCache] = useState([]);
    const [selectedParentClient, setSelectedParentClient] = useState(null);
    const [selectedSalesRep, setSelectedSalesRep] = useState(""); // State for selected sales rep
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const navigate = useNavigate();

    const clientFilter = createFilterOptions({
        matchFrom: "any",
        stringify: (option) => option?.name || "",
    });

    // State to store client name

    const [lead, setLead] = useState({
        source_lead_referred_by: "",
        payment_ref: "",
        frequency_of_service: "",
        special_notes: "",
        status: "open",
    });

    const service_types = [
        "General Pest Control",
        "Termite Control",
        "QPMS",
        "Disinfection",
    ];

    const handleLeadChange = (field, value) => {
        setLead((prev) => ({
            ...prev,
            [field]: value,
        }));
    };
    // Handler to update state on input change

    // State to store client name
    const [client, setClient] = useState({
        name: "",
        addresses: [
            {
                id: generateId(),
                area: "",
                billing: true,
                house_unit_street: "",
                barangay: "",
                city: "",
                zipcode: "",
                latitude: "",
                longitude: "",
            },
        ],
        contacts: [
            {
                id: generateId(),
                firstname: "",
                lastname: "",
                billing: true,
                birthday: "",
                numbers: [
                    {
                        id: generateId(),
                        type: "mobile",
                        number: "",
                    },
                ],
                emails: [
                    {
                        id: generateId(),
                        email: "",
                    },
                ],
            },
        ],
        category: "",
        parent_client: "",
        service_types: [],
        location_type: "",
    });

    const [jobOrder, setJobOrder] = useState({
        client_id: "",
        client: { id: "", name: "", address: {}, contact: {} },
        competencies: [],
        specialization: { general_pest_control: [], termite: [] },
        job_date: "",
        start_time: "",
        end_time: "",
        status: "Not Done",
        service_report_status: "Not Done",
        billing_record_status: "Not Done",
    });

    // Handler to update state on input change

    const [fullZipCodeData, setFullZipCodeData] = useState([]);
    const [barangays, setBarangays] = useState([]);
    const [cities, setCities] = useState([]);
    const [zipCodes, setZipCodes] = useState([]);

    const [showJobOrder, setShowJobOrder] = useState(false); // State to toggle display

    const handleDisplayJobOrder = () => {
        setShowJobOrder((prev) => !prev); // Toggle the state
    };

    useEffect(() => {
        async function fetchFullZipCodeData() {
            try {
                const fullZipCodeData = await getZipCodes(); // Replace with actual endpoint

                // Normalize data by trimming spaces in barangay and city
                const normalizedData = fullZipCodeData.map((data) => ({
                    ...data,
                    barangay: data.barangay.trim(),
                    city: data.city.trim(),
                }));

                // Extract unique barangays, cities, and zip codes
                const uniqueBarangays = [
                    ...new Set(normalizedData.map((data) => data.barangay)),
                ].sort((a, b) => a.localeCompare(b));

                const uniqueCities = [
                    ...new Set(normalizedData.map((data) => data.city)),
                ].sort((a, b) => a.localeCompare(b));

                const uniqueZipCodes = [
                    ...new Set(normalizedData.map((data) => data.zip_code)),
                ].sort((a, b) => a.localeCompare(b));

                setFullZipCodeData(normalizedData);
                setBarangays(uniqueBarangays);
                setCities(uniqueCities);
                setZipCodes(uniqueZipCodes);
            } catch (error) {
                console.error("Error fetching zip codes:", error);
            }
        }

        fetchFullZipCodeData();

        async function fetchSalesReps() {
            const reps = await getSalesReps();
            if (reps) {
                setSalesReps(reps);
            }
        }
        fetchSalesReps();

        const fetchCacheClients = async () => {
            try {
                // Fetch clients
                const raw = await getCacheClients();
                // normalize + de-dupe by ID
                const cleaned = (Array.isArray(raw) ? raw : []).map(c => ({
                    ...c,
                    name: c?.name ? c.name.trim() : c?.name,
                }));
                const byId = new Map();
                for (const c of cleaned) {
                    if (c?.id && !byId.has(c.id)) byId.set(c.id, c);
                }
                const unique = Array.from(byId.values())
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                setClientsCache(unique);
            } catch (err) { }
        };

        fetchCacheClients();
    }, []);

    useEffect(() => {
        setJobOrder((prevJobOrder) => ({
            ...prevJobOrder,
            client: {
                ...prevJobOrder.client,
                address: client.addresses.length === 1 ? client.addresses[0] : {},
                contact: {
                    ...(client.contacts.length === 1 ? client.contacts[0] : {}),
                    number:
                        client.contacts.length === 1 &&
                            client.contacts[0].numbers.length === 1
                            ? client.contacts[0].numbers[0]
                            : {},
                    email:
                        client.contacts.length === 1 &&
                            client.contacts[0].emails.length === 1
                            ? client.contacts[0].emails[0]
                            : {},
                },
            },
        }));
    }, [client]);

    const handleClientNameChange = (value) => {
        setClient((prev) => ({
            ...prev,
            name: value,
        }));
    };

    const handleAddressChange = (index, field, value) => {
        setClient((prev) => {
            const updatedAddresses = [...prev.addresses];
            updatedAddresses[index] = {
                ...updatedAddresses[index],
                [field]: value,
            };

            if (field === "zipcode") {
                // Update latitude and longitude based on the selected zip code
                const { barangay, city } = updatedAddresses[index];
                const selectedDetails = fullZipCodeData.find(
                    (data) =>
                        data.barangay === barangay &&
                        data.city === city &&
                        data.zip_code === value
                );

                if (selectedDetails) {
                    updatedAddresses[index].latitude = selectedDetails.latitude;
                    updatedAddresses[index].longitude = selectedDetails.longitude;
                } else {
                    updatedAddresses[index].latitude = "";
                    updatedAddresses[index].longitude = "";
                }
            }

            return {
                ...prev,
                addresses: updatedAddresses,
            };
        });
    };

    const updateLatLong = (index, updatedAddresses) => {
        const { barangay, city, zipcode } = updatedAddresses[index];

        // Find the matching details in fullZipCodeData
        const selectedDetails = fullZipCodeData.find(
            (data) =>
                data.barangay === barangay &&
                data.city === city &&
                data.zip_code === zipcode
        );

        if (selectedDetails) {
            // Update latitude and longitude if details are found
            updatedAddresses[index].latitude = selectedDetails.latitude;
            updatedAddresses[index].longitude = selectedDetails.longitude;
        } else {
            // Clear latitude and longitude if no match is found
            updatedAddresses[index].latitude = "";
            updatedAddresses[index].longitude = "";
        }

        return updatedAddresses;
    };

    const handleBarangayChange = (index, selectedBarangay) => {
        if (!selectedBarangay) {
            setClient((prevClient) => {
                const updatedAddresses = [...prevClient.addresses];
                updatedAddresses[index] = {
                    ...updatedAddresses[index],
                    barangay: "",
                    city: "",
                    zipcode: "",
                    latitude: "",
                    longitude: "",
                    house_unit_street: "",
                };
                return {
                    ...prevClient,
                    addresses: updatedAddresses,
                };
            });
            return;
        }

        const linkedData = fullZipCodeData.filter(
            (data) => data.barangay === selectedBarangay
        );
        const uniqueCities = [...new Set(linkedData.map((data) => data.city))];

        if (uniqueCities.length === 1) {
            const selectedCity = uniqueCities[0];
            const cityData = linkedData.find((data) => data.city === selectedCity);

            setClient((prevClient) => {
                const updatedAddresses = [...prevClient.addresses];
                updatedAddresses[index] = {
                    ...updatedAddresses[index],
                    barangay: selectedBarangay,
                    city: selectedCity,
                    zipcode: cityData.zip_code,
                    latitude: cityData.latitude,
                    longitude: cityData.longitude,
                };
                return {
                    ...prevClient,
                    addresses: updatedAddresses,
                };
            });
        } else {
            setClient((prevClient) => {
                const updatedAddresses = [...prevClient.addresses];
                updatedAddresses[index] = {
                    ...updatedAddresses[index],
                    barangay: selectedBarangay,
                    city: "",
                    zipcode: "",
                    latitude: "",
                    longitude: "",
                };
                return {
                    ...prevClient,
                    addresses: updatedAddresses,
                };
            });
        }

        // Update state for city dropdown options
        setCities(uniqueCities);
    };

    const handleCityChange = (index, selectedCity) => {
        if (!selectedCity) {
            setClient((prevClient) => {
                const updatedAddresses = [...prevClient.addresses];
                updatedAddresses[index] = {
                    ...updatedAddresses[index],
                    city: "",
                    zipcode: "",
                    latitude: "",
                    longitude: "",
                };
                return {
                    ...prevClient,
                    addresses: updatedAddresses,
                };
            });
            return;
        }

        const filteredZipCodes = fullZipCodeData.filter(
            (data) =>
                data.barangay === client.addresses[index].barangay &&
                data.city === selectedCity
        );

        if (filteredZipCodes.length === 1) {
            const zipCodeData = filteredZipCodes[0];

            setClient((prevClient) => {
                const updatedAddresses = [...prevClient.addresses];
                updatedAddresses[index] = {
                    ...updatedAddresses[index],
                    city: selectedCity,
                    zipcode: zipCodeData.zip_code,
                    latitude: zipCodeData.latitude,
                    longitude: zipCodeData.longitude,
                };
                return {
                    ...prevClient,
                    addresses: updatedAddresses,
                };
            });
        } else {
            setClient((prevClient) => {
                const updatedAddresses = [...prevClient.addresses];
                updatedAddresses[index] = {
                    ...updatedAddresses[index],
                    city: selectedCity,
                    zipcode: "",
                    latitude: "",
                    longitude: "",
                };
                return {
                    ...prevClient,
                    addresses: updatedAddresses,
                };
            });

            // Update state for zip code dropdown options
            setZipCodes(filteredZipCodes.map((data) => data.zip_code));
        }
    };

    // Handler for changing the checkbox (only one can be selected)
    const handleAddressCheckboxChange = (index) => {
        setClient((prev) => ({
            ...prev,
            addresses: prev.addresses.map((address, i) => ({
                ...address,
                billing: i === index, // Set billing_address to true only for the selected index
            })),
        }));
    };

    // Handler to add a new address row
    const handleAddAddress = () => {
        setClient((prev) => ({
            ...prev,
            addresses: [
                ...prev.addresses,
                {
                    id: generateId(),
                    area: "",
                    billing: false,
                    house_unit_street: "",
                    barangay: "",
                    city: "",
                    zipcode: "",
                    latitude: "",
                    longitude: "",
                },
            ],
        }));
    };

    // Handler to remove an address row
    const handleRemoveAddress = (index) => {
        setClient((prev) => {
            const updatedAddresses = prev.addresses.filter(
                (_, addrIndex) => addrIndex !== index
            );

            // If only one address remains, set its billing field to true
            if (updatedAddresses.length === 1) {
                updatedAddresses[0] = { ...updatedAddresses[0], billing: true };
            }

            return {
                ...prev,
                addresses: updatedAddresses,
            };
        });
    };

    // Handler to update contact fields
    const handleContactChange = (index, field, value) => {
        setClient((prev) => ({
            ...prev,
            contacts: prev.contacts.map((contact, i) =>
                i === index ? { ...contact, [field]: value } : contact
            ),
        }));
    };

    const handleAddContact = () => {
        setClient((prev) => ({
            ...prev,
            contacts: [
                ...prev.contacts,
                {
                    id: generateId(),
                    firstname: "",
                    lastname: "",
                    billing: false,
                    birthday: "",
                    numbers: [
                        {
                            id: generateId(),
                            type: "mobile",
                            number: "",
                        },
                    ],
                    emails: [
                        {
                            id: generateId(),
                            email: "",
                        },
                    ],
                },
            ],
        }));
    };

    // Handler to remove a contact person
    const handleRemoveContact = (index) => {
        setClient((prev) => {
            const updatedContacts = prev.contacts.filter(
                (_, contactIndex) => contactIndex !== index
            );

            // If only one address remains, set its billing field to true
            if (updatedContacts.length === 1) {
                updatedContacts[0] = { ...updatedContacts[0], billing: true };
            }

            return {
                ...prev,
                contacts: updatedContacts,
            };
        });
    };

    const handleContactCheckboxChange = (index) => {
        setClient((prev) => ({
            ...prev,
            contacts: prev.contacts.map((contact, i) => ({
                ...contact,
                billing: i === index, // Set billing_address to true only for the selected index
            })),
        }));
    };

    const handleContactDetailsChange = (
        contactIndex,
        field,
        subIndex,
        subField,
        value
    ) => {
        setClient((prev) => ({
            ...prev,
            contacts: prev.contacts.map((contact, i) =>
                i === contactIndex
                    ? {
                        ...contact,
                        [field]: contact[field].map((item, j) =>
                            j === subIndex ? { ...item, [subField]: value } : item
                        ),
                    }
                    : contact
            ),
        }));
    };

    const handleAddNumber = (contactIndex) => {
        setClient((prev) => ({
            ...prev,
            contacts: prev.contacts.map((contact, i) =>
                i === contactIndex
                    ? {
                        ...contact,
                        numbers: [
                            ...contact.numbers,
                            { id: generateId(), type: "mobile", number: "" },
                        ],
                    }
                    : contact
            ),
        }));
    };

    const handleRemoveNumber = (contactIndex, numberIndex) => {
        setClient((prev) => ({
            ...prev,
            contacts: prev.contacts.map((contact, i) =>
                i === contactIndex
                    ? {
                        ...contact,
                        numbers:
                            contact.numbers.length > 1
                                ? contact.numbers.filter((_, j) => j !== numberIndex)
                                : contact.numbers, // Keep at least one number if only one remains
                    }
                    : contact
            ),
        }));
    };

    const handleAddEmail = (contactIndex) => {
        setClient((prev) => ({
            ...prev,
            contacts: prev.contacts.map((contact, i) =>
                i === contactIndex
                    ? {
                        ...contact,
                        emails: [...contact.emails, { id: generateId(), email: "" }],
                    }
                    : contact
            ),
        }));
    };

    const handleRemoveEmail = (contactIndex, emailIndex) => {
        setClient((prev) => ({
            ...prev,
            contacts: prev.contacts.map((contact, i) =>
                i === contactIndex
                    ? {
                        ...contact,
                        emails:
                            contact.emails.length > 1
                                ? contact.emails.filter((_, j) => j !== emailIndex)
                                : contact.emails, // Keep at least one number if only one remains
                    }
                    : contact
            ),
        }));
    };

    const handleSalesRepChange = (event) => {
        setSelectedSalesRep(event.target.value);
        setLead((prev) => ({
            ...prev,
            sales_rep_id: event.target.value,
        }));
    };

    const competencies = [
        "General Pest Control",
        "Termite Control",
        "QPMS",
        "Disinfection",
        "Basic Safety",
    ];

    const handleCompetencyChange = (e, formattedOption) => {
        setJobOrder((prev) => {
            const isChecked = e.target.checked; // Extract checked status
            const updatedCompetencies = isChecked
                ? [...prev.competencies, formattedOption] // Add formattedOption if checked
                : prev.competencies.filter((option) => option !== formattedOption); // Remove if unchecked

            // Return early if there's no change to prevent unnecessary re-renders
            if (updatedCompetencies === prev.competencies) {
                return prev;
            }

            return {
                ...prev, // Preserve other jobOrder properties
                competencies: updatedCompetencies,
            };
        });
    };

    const specializationGPC = [
        "Rodent",
        "Baiting",
        "Cockroach",
        "Fly Control",
        "Ant Control",
        "Larviciding",
    ];

    const specializationTermite = [
        "Compre",
        "AG",
        "IG",
        "IC",
        "Spraying",
        "Soil Poisoning",
    ];

    const handleSpecializationChange = (type, e, formattedOption) => {
        const { checked } = e.target;

        setJobOrder((prev) => {
            const updatedSpecialization = checked
                ? [...prev.specialization[type], formattedOption] // Add formattedOption if checked
                : prev.specialization[type].filter(
                    (option) => option !== formattedOption
                ); // Remove formattedOption if unchecked

            // Prevent unnecessary updates if there are no changes
            if (updatedSpecialization === prev.specialization[type]) {
                return prev;
            }

            return {
                ...prev,
                specialization: {
                    ...prev.specialization,
                    [type]: updatedSpecialization, // Dynamically update the specialization type
                },
            };
        });
    };

    const handleJobOrderScheduleChange = (field, value) => {
        setJobOrder((prev) => ({
            ...prev, // Preserve the rest of the jobOrder
            [field]: value, // Dynamically update the field based on the `field` variable
        }));
    };

    const handleContactNumberChange = (selectedNumber) => {
        setJobOrder((prev) => ({
            ...prev, // Preserve the rest of the jobOrder
            client: {
                ...prev.client, // Preserve other properties in client
                contact: {
                    ...prev.client.contact, // Preserve other properties in contact
                    number: selectedNumber, // Update the number field with the new object
                },
            },
        }));
    };

    const handleContactEmailChange = (selectedEmail) => {
        setJobOrder((prev) => ({
            ...prev, // Preserve the rest of the jobOrder
            client: {
                ...prev.client, // Preserve other properties in client
                contact: {
                    ...prev.client.contact, // Preserve other properties in contact
                    email: selectedEmail, // Update the email field with the selected object
                },
            },
        }));
    };

    const handleJobOrderAddressChange = (selectedAddress) => {
        setJobOrder((prev) => ({
            ...prev, // Preserve the rest of the jobOrder
            client: {
                ...prev.client, // Preserve other properties in client
                address: selectedAddress, // Update the address field with the new object
            },
        }));
    };

    const handleJobOrderContactChange = (selectedContact) => {
        const updatedContact = selectedContact
            ? {
                ...selectedContact,
                number:
                    selectedContact.numbers?.length === 1
                        ? selectedContact.numbers[0]
                        : "",
                email:
                    selectedContact.emails?.length === 1
                        ? selectedContact.emails[0]
                        : "",
            }
            : {};

        setJobOrder((prev) => ({
            ...prev, // Preserve the rest of the jobOrder
            client: {
                ...prev.client, // Preserve other properties in client
                contact: updatedContact, // Update the contact field with the new object
            },
        }));
    };

    const handleJobOrderContactNumberChange = (selectedNumber) => {
        setJobOrder((prev) => ({
            ...prev, // Preserve the rest of the jobOrder
            client: {
                ...prev.client, // Preserve other properties in client
                contact: {
                    ...prev.client.contact, // Preserve other properties in contact
                    number: selectedNumber, // Update the number field with the new object
                },
            },
        }));
    };

    const handleJobOrderContactEmailChange = (selectedEmail) => {
        setJobOrder((prev) => ({
            ...prev, // Preserve the rest of the jobOrder
            client: {
                ...prev.client, // Preserve other properties in client
                contact: {
                    ...prev.client.contact, // Preserve other properties in contact
                    email: selectedEmail, // Update the email field with the selected object
                },
            },
        }));
    };

    const handleCategoryChange = (value) => {
        setClient((prev) => ({
            ...prev,
            category: value,
        }));
    };

    const handleSelectedParentClient = (event, value) => {
        setSelectedParentClient(value || null); // Update the selected client
        setClient((prev) => ({
            ...prev,
            parent_client: value,
        }));
    };

    const handleServiceTypeChange = (e, formattedOption) => {
        setClient((prev) => {
            const isChecked = e.target.checked; // Extract checked status
            const updatedServiceTypes = isChecked
                ? [...prev.service_types, formattedOption] // Add formattedOption if checked
                : prev.service_types.filter((option) => option !== formattedOption); // Remove if unchecked

            // Return early if there's no change to prevent unnecessary re-renders
            if (updatedServiceTypes === prev.service_types) {
                return prev;
            }

            return {
                ...prev, // Preserve other jobOrder properties
                service_types: updatedServiceTypes,
            };
        });
    };

    const handleLocationTypeChange = (value) => {
        setClient((prev) => ({
            ...prev,
            location_type: value,
        }));
    };
    async function handleSubmit(e) {
        e.preventDefault();
        setIsLoading(true);

        // Clone initial objects
        const clientWithId = { ...client };
        const updatedJobOrder = { ...jobOrder };
        let jobOrder_id = "";
        const updatedLead = { ...lead };

        try {
            // Post the client and parse its response
            const clientResponse = await postClient(client);
            const clientResult = await clientResponse.json();

            // Ensure the client creation was successful
            if (
                clientResult &&
                clientResult.created_clients &&
                clientResult.created_clients.length > 0
            ) {
                const client_id = clientResult.created_clients[0].id;
                clientWithId.id = client_id;

                // If a job order should be created, update and post it
                if (showJobOrder) {
                    updatedJobOrder.client_id = client_id;
                    // Make sure updatedJobOrder has a 'client' object
                    if (!updatedJobOrder.client) {
                        updatedJobOrder.client = {};
                    }
                    updatedJobOrder.client.id = client_id;
                    updatedJobOrder.client.name = clientWithId.name;

                    const jobOrderResponse = await postJobOrder(updatedJobOrder);
                    // If jobOrderResponse is a fetch response, parse it as JSON;
                    // otherwise, assume it's already a parsed object.
                    const jobOrderResult = jobOrderResponse.json
                        ? await jobOrderResponse.json()
                        : jobOrderResponse;

                    if (
                        jobOrderResult &&
                        jobOrderResult.created_job_orders &&
                        jobOrderResult.created_job_orders.length > 0
                    ) {
                        jobOrder_id = jobOrderResult.created_job_orders[0].id;
                    }
                }

                // Prepare the updated lead with client and job order details
                updatedLead.client_id = client_id;
                updatedLead.client = clientWithId;
                updatedLead.job_order_id = jobOrder_id;

                console.log("Updated Lead:", updatedLead);
                await postLead(updatedLead);
            } else {
                console.error("No clients were created or the response is invalid.");
            }
        } catch (error) {
            console.error("Error occurred during submission:", error);
            // Optionally, add UI feedback for the error here (e.g., flash message)
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <BaseFormWrapper
            titleIcon={<TrackChangesIcon />}
            title="Sales"
            subTitle="Manage Leads"
            subTitleRoute="/manage-leads"
            formTitle="Add New Lead"
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            isLoading={isLoading}
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(e);
                }}
                autoComplete="off"
            >
                <Grid container>
                    {/* Client Information Section */}
                    <Grid
                        item
                        xs={12}
                        md={12}
                        sx={{ display: "flex", flexDirection: "column" }}
                    >
                        <Card
                            variant="outlined"
                            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                        >
                            <CardHeader
                                title={<Typography variant="h6">Client Information</Typography>}
                            />
                            <CardContent
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Grid container spacing={2}>
                                    {/* Client Name */}
                                    <Grid item xs={4}>
                                        <ClientName
                                            clientName={client.name}
                                            handleClientNameChange={handleClientNameChange}
                                        />
                                    </Grid>

                                    {/* Address / Job Site Address */}
                                    <Grid item xs={12}>
                                        <ClientAddress
                                            clientAddresses={client.addresses}
                                            fullZipCodeData={fullZipCodeData}
                                            barangays={barangays}
                                            cities={cities}
                                            zipCodes={zipCodes}
                                            handleAddressChange={handleAddressChange}
                                            handleBarangayChange={handleBarangayChange}
                                            handleCityChange={handleCityChange}
                                            handleAddressCheckboxChange={handleAddressCheckboxChange}
                                            handleAddAddress={handleAddAddress}
                                            handleRemoveAddress={handleRemoveAddress}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Contact Person Section */}
                    <Grid
                        item
                        xs={12}
                        md={12}
                        sx={{ display: "flex", flexDirection: "column" }}
                    >
                        <Card
                            variant="outlined"
                            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                        >
                            <CardHeader
                                title={<Typography variant="h6">Contact Person</Typography>}
                            />
                            <CardContent
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Grid item xs={12}>
                                    <ClientContact
                                        clientContacts={client.contacts}
                                        handleContactChange={handleContactChange}
                                        handleAddContact={handleAddContact}
                                        handleRemoveContact={handleRemoveContact}
                                        handleContactCheckboxChange={handleContactCheckboxChange}
                                        handleContactDetailsChange={handleContactDetailsChange}
                                        handleAddNumber={handleAddNumber}
                                        handleRemoveNumber={handleRemoveNumber}
                                        handleAddEmail={handleAddEmail}
                                        handleRemoveEmail={handleRemoveEmail}
                                    />
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={12}
                        sx={{ display: "flex", flexDirection: "column" }}
                    >
                        <Card
                            variant="outlined"
                            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                        >
                            <CardHeader
                                title={
                                    <Typography variant="h6">Client Classifications</Typography>
                                }
                            />
                            <CardContent
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2}>
                                            <FormControl fullWidth>
                                                <InputLabel
                                                    id="category-label"
                                                    sx={{ fontSize: "0.875rem" }}
                                                >
                                                    Category
                                                </InputLabel>
                                                <Select
                                                    labelId="category-label"
                                                    value={client.category}
                                                    onChange={(e) => handleCategoryChange(e.target.value)}
                                                    label="Category"
                                                    sx={{
                                                        fontSize: "0.875rem", // Adjust font size for select
                                                        "& .MuiSelect-select": { fontSize: "0.875rem" }, // Font size for the selected value
                                                    }}
                                                >
                                                    <MenuItem value="VVIP" sx={{ fontSize: "0.875rem" }}>
                                                        VVIP
                                                    </MenuItem>
                                                    <MenuItem value="VIP" sx={{ fontSize: "0.875rem" }}>
                                                        VIP
                                                    </MenuItem>
                                                    <MenuItem value="Gold" sx={{ fontSize: "0.875rem" }}>
                                                        Gold
                                                    </MenuItem>
                                                    <MenuItem
                                                        value="Silver"
                                                        sx={{ fontSize: "0.875rem" }}
                                                    >
                                                        Silver
                                                    </MenuItem>
                                                    <MenuItem
                                                        value="Bronze"
                                                        sx={{ fontSize: "0.875rem" }}
                                                    >
                                                        Bronze
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Autocomplete
                                                options={clientsCache}
                                                getOptionLabel={(option) => option?.name || ""}
                                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                filterOptions={(options, params) => clientFilter(options, params)}
                                                openOnFocus
                                                autoHighlight
                                                value={selectedParentClient || null}
                                                onChange={(event, newValue) => handleSelectedParentClient(event, newValue)}
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option.id}>
                                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                                        <span>{option.name}</span>
                                                        <small style={{ opacity: 0.7 }}>ID: {option.id}</small>
                                                    </div>
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                    {...params}
                                                    label="Parent Client"
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{
                                                        "& .MuiInputBase-input": { fontSize: "0.875rem" },
                                                        "& .MuiFormLabel-root": { fontSize: "0.875rem" },
                                                        "& .MuiAutocomplete-listbox": { fontSize: "0.875rem" },
                                                        "& .MuiFormHelperText-root": { fontSize: "0.75rem" },
                                                    }}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography
                                                variant="body2" // Smaller text variant
                                                sx={{
                                                    paddingBottom: "0.5rem", // Adjust spacing as needed
                                                    fontWeight: "normal", // Remove bold for label-like appearance
                                                    textTransform: "none", // Keep natural casing if preferred
                                                    fontSize: "0.875rem", // Smaller font size for a label
                                                    color: "gray", // Change the text color to gray
                                                }}
                                            >
                                                Service Type
                                            </Typography>
                                            {service_types.map((option) => {
                                                const formattedOption = option
                                                    .toLowerCase()
                                                    .replace(/\s+/g, "_");
                                                return (
                                                    <FormControlLabel
                                                        key={formattedOption}
                                                        control={
                                                            <Checkbox
                                                                name={formattedOption}
                                                                checked={client.service_types.includes(
                                                                    formattedOption
                                                                )}
                                                                onChange={(e) =>
                                                                    handleServiceTypeChange(e, formattedOption)
                                                                }
                                                            />
                                                        }
                                                        label={option}
                                                    />
                                                );
                                            })}
                                        </Grid>
                                        <Grid item xs={3}>
                                            <FormControl fullWidth>
                                                <InputLabel
                                                    id="category-label"
                                                    sx={{ fontSize: "0.875rem" }}
                                                >
                                                    Location Type
                                                </InputLabel>
                                                <Select
                                                    labelId="category-label"
                                                    value={client.location_type}
                                                    onChange={(e) =>
                                                        handleLocationTypeChange(e.target.value)
                                                    }
                                                    label="Location Type"
                                                    sx={{
                                                        fontSize: "0.875rem", // Adjust font size for select
                                                        "& .MuiSelect-select": { fontSize: "0.875rem" }, // Font size for the selected value
                                                    }}
                                                >
                                                    <MenuItem
                                                        value="residential"
                                                        sx={{ fontSize: "0.875rem" }}
                                                    >
                                                        Residential
                                                    </MenuItem>
                                                    <MenuItem
                                                        value="corporate"
                                                        sx={{ fontSize: "0.875rem" }}
                                                    >
                                                        Corporate
                                                    </MenuItem>
                                                    <MenuItem value="hotel" sx={{ fontSize: "0.875rem" }}>
                                                        Hotel
                                                    </MenuItem>
                                                    <MenuItem
                                                        value="restaurant"
                                                        sx={{ fontSize: "0.875rem" }}
                                                    >
                                                        Restaurant
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Sales Information Section */}
                    <Grid
                        item
                        xs={12}
                        md={12}
                        sx={{ display: "flex", flexDirection: "column" }}
                    >
                        <Card
                            variant="outlined"
                            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                        >
                            <CardHeader
                                title={<Typography variant="h6">Sales Information</Typography>}
                            />
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={4}>
                                                <TextField
                                                    label="Source Lead / Referred by"
                                                    name="sourceLeadreferredBy"
                                                    value={lead.source_lead_referred_by} // Bind state to the input value
                                                    onChange={(e) =>
                                                        handleLeadChange(
                                                            "source_lead_referred_by",
                                                            e.target.value
                                                        )
                                                    } // Update state on input change
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <TextField
                                                    label="Payment Ref"
                                                    name="paymentRef"
                                                    value={lead.payment_ref} // Bind state to the input value
                                                    onChange={(e) =>
                                                        handleLeadChange("payment_ref", e.target.value)
                                                    } // Update state on input change
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <TextField
                                                    label="Frequency of Service"
                                                    name="frequencyOfService"
                                                    value={lead.frequency_of_service} // Bind state to the input value
                                                    onChange={(e) =>
                                                        handleLeadChange(
                                                            "frequency_of_service",
                                                            e.target.value
                                                        )
                                                    } // Update state on input change
                                                    fullWidth
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={4}>
                                                <FormControl fullWidth>
                                                    <InputLabel id="sales-rep-label">
                                                        Sales Representative
                                                    </InputLabel>
                                                    <Select
                                                        labelId="sales-rep-label"
                                                        value={selectedSalesRep}
                                                        onChange={handleSalesRepChange}
                                                        label="Sales Representative"
                                                    >
                                                        {salesReps.map((rep) => (
                                                            <MenuItem key={rep.id} value={rep.id}>
                                                                {`${rep.firstname} ${rep.lastname} - ${rep.branch}`}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={2}>
                                                <FormControl fullWidth>
                                                    <InputLabel id="status-label">Status</InputLabel>
                                                    <Select
                                                        labelId="status-label"
                                                        value={lead.status}
                                                        onChange={(e) =>
                                                            handleLeadChange("status", e.target.value)
                                                        }
                                                        label="Status"
                                                    >
                                                        <MenuItem value="open">Open</MenuItem>
                                                        <MenuItem value="contacted">Contacted</MenuItem>
                                                        <MenuItem value="ocular">Ocular</MenuItem>
                                                        <MenuItem value="closed">Closed</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Special Notes Section */}
                    <Grid
                        item
                        xs={12}
                        md={12}
                        sx={{ display: "flex", flexDirection: "column" }}
                    >
                        <Card
                            variant="outlined"
                            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                        >
                            <CardHeader
                                title={<Typography variant="h6">Special Notes</Typography>}
                            />
                            <CardContent>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Special Notes"
                                        name="specialNotes"
                                        value={lead.special_notes} // Bind state to the input value
                                        onChange={(e) =>
                                            handleLeadChange("special_notes", e.target.value)
                                        } // Update state on input change
                                        fullWidth
                                        multiline
                                        rows={4}
                                    />
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Job Order Section */}
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDisplayJobOrder}
                            size="small"
                            sx={{ fontSize: "0.875rem" }}
                        >
                            {showJobOrder ? "Hide Job Order" : "Schedule Job Order"}
                        </Button>
                    </Grid>
                    {showJobOrder && ( // Conditionally render Job Order fields
                        <>
                            <Grid
                                item
                                xs={12}
                                md={12}
                                sx={{ display: "flex", flexDirection: "column" }}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{
                                        padding: "20px",
                                        marginBottom: "20px",
                                        marginTop: "20px",
                                        flex: 1,
                                    }}
                                >
                                    <CardHeader
                                        title={<Typography variant="h6">Job Order</Typography>}
                                    />

                                    <CardContent
                                        sx={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={10}>
                                                        <FormControl fullWidth disabled={!client}>
                                                            <InputLabel id="job-site-address-label">
                                                                Job Site Address
                                                            </InputLabel>
                                                            <Select
                                                                labelId="job-site-address"
                                                                value={jobOrder?.client?.address?.id || ""}
                                                                onChange={(e) => {
                                                                    const selectedAddress =
                                                                        client?.addresses?.find(
                                                                            (address) => address.id === e.target.value
                                                                        );
                                                                    handleJobOrderAddressChange(selectedAddress); // Pass the full address object
                                                                }}
                                                                label="Job Site Address"
                                                            >
                                                                {(client?.addresses || []).map(
                                                                    ({
                                                                        id,
                                                                        house_unit_street,
                                                                        barangay,
                                                                        city,
                                                                        zipcode,
                                                                    }) => (
                                                                        <MenuItem key={id} value={id}>
                                                                            {[
                                                                                house_unit_street,
                                                                                barangay,
                                                                                city,
                                                                                zipcode,
                                                                            ]
                                                                                .filter(Boolean) // Remove empty or undefined values
                                                                                .join(", ")}{" "}
                                                                            {/* Join only non-empty values */}
                                                                        </MenuItem>
                                                                    )
                                                                )}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>

                                                    <Grid item xs={2}>
                                                        <TextField
                                                            name="area"
                                                            label="Area"
                                                            value={jobOrder?.client?.address?.area || ""}
                                                            fullWidth
                                                            InputProps={{
                                                                readOnly: true, // Set read-only attribute
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item xs={4}>
                                                <FormControl fullWidth disabled={!client}>
                                                    <InputLabel id="contact-person-label">
                                                        Contact Person
                                                    </InputLabel>
                                                    <Select
                                                        labelId="contact-person-label"
                                                        value={jobOrder?.client?.contact?.id || ""}
                                                        onChange={(e) => {
                                                            const selectedContact = client?.contacts?.find(
                                                                (contact) => contact.id === e.target.value
                                                            );
                                                            handleJobOrderContactChange(selectedContact); // Pass the full contact object
                                                        }}
                                                        label="Contact Person"
                                                    >
                                                        {(client?.contacts || []).map(
                                                            ({ id, firstname, lastname }) => (
                                                                <MenuItem key={id} value={id}>
                                                                    {[lastname, firstname]
                                                                        .filter(Boolean) // Remove empty or undefined values
                                                                        .join(", ")}{" "}
                                                                    {/* Join only non-empty values */}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={4}>
                                                <FormControl
                                                    fullWidth
                                                    disabled={!client || !jobOrder?.client?.contact} // Safeguard against undefined jobOrder, client, or contact
                                                >
                                                    <InputLabel id="contact-number-label">
                                                        Contact Number
                                                    </InputLabel>
                                                    <Select
                                                        labelId="contact-number-label"
                                                        value={jobOrder?.client?.contact?.number?.id || ""}
                                                        onChange={(e) => {
                                                            const selectedNumber =
                                                                jobOrder?.client?.contact?.numbers?.find(
                                                                    (number) => number.id === e.target.value
                                                                );
                                                            handleJobOrderContactNumberChange(selectedNumber); // Pass the full number object
                                                        }}
                                                        label="Contact Number"
                                                    >
                                                        {(
                                                            client.contacts.find(
                                                                (contact) =>
                                                                    contact.id === jobOrder?.client?.contact?.id
                                                            )?.numbers || []
                                                        ).map((number) => (
                                                            <MenuItem key={number.id} value={number.id}>
                                                                {number.type}: {number.number}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={4}>
                                                <FormControl
                                                    fullWidth
                                                    disabled={!client || !jobOrder.client.contact}
                                                >
                                                    <InputLabel id="contact-email-label">
                                                        Contact Email
                                                    </InputLabel>
                                                    <Select
                                                        labelId="contact-email-label"
                                                        value={jobOrder.client.contact?.email?.id || ""}
                                                        onChange={(e) => {
                                                            const selectedEmail =
                                                                jobOrder.client.contact.emails.find(
                                                                    (email) => email.id === e.target.value
                                                                );
                                                            handleContactEmailChange(selectedEmail); // Pass the full email object
                                                        }}
                                                        label="Contact Email"
                                                    >
                                                        {(
                                                            client.contacts.find(
                                                                (contact) =>
                                                                    contact.id === jobOrder?.client?.contact?.id
                                                            )?.emails || []
                                                        ).map((email) => (
                                                            <MenuItem key={email.id} value={email.id}>
                                                                {email.email}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Competency Section */}
                            <Grid
                                item
                                xs={12}
                                md={12}
                                sx={{ display: "flex", flexDirection: "column" }}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                                >
                                    <CardHeader
                                        title={<Typography variant="h6">Competency</Typography>}
                                    />
                                    <CardContent>
                                        <Grid item xs={12}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                {competencies.map((option) => {
                                                    const formattedOption = option
                                                        .toLowerCase()
                                                        .replace(/\s+/g, "_");
                                                    return (
                                                        <FormControlLabel
                                                            key={formattedOption}
                                                            control={
                                                                <Checkbox
                                                                    name={formattedOption}
                                                                    checked={jobOrder.competencies.includes(
                                                                        formattedOption
                                                                    )}
                                                                    onChange={(e) =>
                                                                        handleCompetencyChange(e, formattedOption)
                                                                    }
                                                                />
                                                            }
                                                            label={option}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Specialization Section */}
                            <Grid item xs={6} md={6}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        padding: "20px",
                                        marginBottom: "20px",
                                        marginRight: "5px",
                                    }}
                                >
                                    <CardHeader
                                        title={
                                            <Typography variant="h6">
                                                Specialization (General Pest Control)
                                            </Typography>
                                        }
                                    />
                                    <CardContent>
                                        <Grid item xs={12}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                {specializationGPC.map((option) => {
                                                    const formattedOption = option
                                                        .toLowerCase()
                                                        .replace(/\s+/g, "_");
                                                    return (
                                                        <FormControlLabel
                                                            key={formattedOption}
                                                            control={
                                                                <Checkbox
                                                                    name={formattedOption}
                                                                    checked={jobOrder.specialization.general_pest_control.includes(
                                                                        formattedOption
                                                                    )}
                                                                    onChange={(e) =>
                                                                        handleSpecializationChange(
                                                                            "general_pest_control",
                                                                            e,
                                                                            formattedOption
                                                                        )
                                                                    } // Pass `e` and formattedOption
                                                                />
                                                            }
                                                            label={option}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={6} md={6}>
                                <Card
                                    variant="outlined"
                                    sx={{ padding: "20px", marginBottom: "20px" }}
                                >
                                    <CardHeader
                                        title={
                                            <Typography variant="h6">
                                                Specialization (Termite)
                                            </Typography>
                                        }
                                    />
                                    <CardContent>
                                        <Grid item xs={12}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                {specializationTermite.map((option) => {
                                                    const formattedOption = option
                                                        .toLowerCase()
                                                        .replace(/\s+/g, "_");
                                                    return (
                                                        <FormControlLabel
                                                            key={formattedOption}
                                                            control={
                                                                <Checkbox
                                                                    name={formattedOption}
                                                                    checked={jobOrder.specialization.termite.includes(
                                                                        formattedOption
                                                                    )}
                                                                    onChange={(e) =>
                                                                        handleSpecializationChange(
                                                                            "termite",
                                                                            e,
                                                                            formattedOption
                                                                        )
                                                                    } // Pass `e` and formattedOption
                                                                />
                                                            }
                                                            label={option}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Schedule Section */}
                            <Grid item xs={12}>
                                <Card
                                    variant="outlined"
                                    sx={{ padding: "20px", marginBottom: "20px" }}
                                >
                                    <CardHeader
                                        title={<Typography variant="h6">Schedule</Typography>}
                                    />
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            <Grid item xs={2}>
                                                <TextField
                                                    label="Date"
                                                    name="job_date"
                                                    type="date"
                                                    value={jobOrder.job_date || ""}
                                                    onChange={(e) =>
                                                        handleJobOrderScheduleChange(
                                                            "job_date",
                                                            e.target.value
                                                        )
                                                    }
                                                    InputLabelProps={{ shrink: true }}
                                                    fullWidth
                                                />
                                            </Grid>

                                            <Grid item xs={2}>
                                                <TextField
                                                    label="Start Time"
                                                    name="start_time"
                                                    type="time"
                                                    value={jobOrder.start_time || ""}
                                                    onChange={(e) =>
                                                        handleJobOrderScheduleChange(
                                                            "start_time",
                                                            e.target.value
                                                        )
                                                    }
                                                    InputLabelProps={{ shrink: true }}
                                                    fullWidth
                                                />
                                            </Grid>

                                            <Grid item xs={2}>
                                                <TextField
                                                    label="End Time"
                                                    name="end_time"
                                                    type="time"
                                                    value={jobOrder.end_time}
                                                    onChange={(e) =>
                                                        handleJobOrderScheduleChange(
                                                            "end_time",
                                                            e.target.value
                                                        )
                                                    }
                                                    InputLabelProps={{ shrink: true }}
                                                    fullWidth
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </>
                    )}

                    {/* Form buttons */}
                    <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        mt={2}
                        mb={2}
                        mr={2}
                    >
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate("/manage-leads")}
                            sx={{
                                fontSize: "0.875rem",
                                color: "#FF4C4C",
                                borderColor: "#FF4C4C",
                            }}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            sx={{
                                marginLeft: "10px",
                                fontSize: "0.875rem",
                            }}
                            disabled={isLoading}
                        >
                            Submit
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </BaseFormWrapper>        
    );
}

export default AddNewLead;
